import { useTreeStore } from '@/frontend/store/TreeStore';
import { I18n } from '@/i18n/I18n';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { BasicButton } from '../buttons/BasicButton';
import { ButtonParent } from '../buttons/ButtonParent';
import { Operation } from '@/backend/models/operation';
import { Fullscreen } from '@/frontend/SVGs/Fullscreen';
import { CommandingEditor } from './CommandingEditor';
import { CollapseButton } from '../buttons/CollapseButton';
import { ParameterType, ParameterSubjectType } from '@/types/parameterTypes';
import { ParameterTable } from '../parameters/ParameterTable';

export function CommandingItem(props: { operation: Operation }) {
  const { operation } = props;
  const commanding = operation.commanding;
  const [activeTab, setActiveTab] = useState<'input' | 'output' | 'fullscreen'>('input');
  const [showTasks, setShowTasks] = useState(commanding?.inputTasks.length === 0 && commanding?.outputTasks.length === 0);
  
  if(!commanding) return null;

  return (
    <div className="border rounded bg-light mt-3 p-2">
        <div className='d-flex align-items-center'>
          <CollapseButton showElement={showTasks} setShowElement={setShowTasks} title={I18n.get('commandingTasks')} />
          <span className="fw-semibold text-muted">{I18n.get('commandingTasks')}</span>
        </div>
      {showTasks && <>
        <ul className="nav nav-tabs w-100">
          <li className="nav-item">
            <BasicButton
              className={`nav-link ${activeTab === 'input' ? 'active' : ''}`}
              onClick={() => setActiveTab('input')}
              label={I18n.get('inputTasks')}
            />
          </li>
          <li className="nav-item">
            <BasicButton
              className={`nav-link ${activeTab === 'output' ? 'active' : ''}`}
              onClick={() => setActiveTab('output')}
              label={I18n.get('outputTasks')}
            />
          </li>
          <li className="nav-item">
            <ButtonParent
              className={`nav-link ${activeTab === 'fullscreen' ? 'active' : ''}`}
              onClick={() => setActiveTab('fullscreen')}
              title={I18n.get('fullscreen')}
              style={{ maxHeight: '40px', minHeight: '32px' }}
            >
              <Fullscreen />
            </ButtonParent>
          </li>
        </ul>
        <div className="tab-content w-100">
          {activeTab === 'input' && (
            <div className="tab-pane fade show active w-100">
              <div className="form-floating mt-3 commandingEditor" style={{ maxWidth: '65vw' }}>
                <InputTasksEditor operation={operation} />
              </div>
            </div>
          )}
          {activeTab === 'output' && (
            <div className="tab-pane fade show active w-100">
              <div className="form-floating mt-3 commandingEditor" style={{ maxWidth: '65vw' }}>
                <OutputTasksEditor operation={operation} />
              </div>
            </div>
          )}
        </div>
        <div className='align-self-start w-100'>
          <ParameterTable subjectId={operation.id} parameterType={ParameterType.OUTPUT} subjectType={ParameterSubjectType.OPERATION} />
        </div>
      </>}
      <Modal
        show={activeTab === 'fullscreen'}
        onHide={() => setActiveTab('input')}
        dialogClassName="modal-fixed-width"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{I18n.get('commandingEditor')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-fixed">
          <div className="row flex-grow-1">
            <div className="col-6">
              <h5>{I18n.get('inputTasks')}:</h5>
              <div className="flex-grow-1">
                  <InputTasksEditor operation={operation} />
              </div>
            </div>
            <div className="col-6">
              <h5>{I18n.get('Output Tasks')}:</h5>
              <div className="flex-grow-1">
                <OutputTasksEditor operation={operation} />
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

function InputTasksEditor(props: { operation: Operation }) {
  const { operation } = props;
  const commanding = operation.commanding;
  const updateCommanding = useTreeStore((state) => state.updateCommanding);

  if(!commanding) return null;

  return (
    <CommandingEditor
      value={commanding.inputTasks}
      onBlur={(value: string) => updateCommanding({ ...commanding, inputTasks: value }, operation)}
      label={I18n.get('editInputTasks')}
      operation={operation}
    />
  );
}

function OutputTasksEditor(props: { operation: Operation }) {
  const { operation } = props;
  const commanding = operation.commanding;
  const updateCommanding = useTreeStore((state) => state.updateCommanding);

  if(!commanding) return null;

  return (
    <CommandingEditor
      value={commanding.outputTasks}
      onBlur={(value: string) => updateCommanding({ ...commanding, outputTasks: value }, operation)}
      label={I18n.get('editOutputTasks')}
      operation={operation}
    />
  );
}
